import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { Type } from '@warp-drive/core-types/symbols';
import type { Carecommendation } from 'lumen/models';
import type { RateLike } from 'lumen/services/hotel-and-market';
import {
  type RECOMMENDATION_REASON_TYPE_COMPSET,
  RECOMMENDATION_SUMMARISED_REASONS,
  RECOMMENDATION_SUMMARISED_REASONS_TRANSLATION_KEY_MAPPING,
  type RecommendationReasonTranslationKey,
  type RecommendationReasonTypeCompset,
  type RecommendationReasonTypeOccupancy,
  type RecommendationReasonTypeUserRule,
  type RecommendationSummarisedReasons,
} from 'lumen/utils/constants/pricing-assistant-constants';

export type CompsetRecommendationReasonDetailFallbackReason = {
  formattedDetail: string;
  translation_key: RecommendationReasonTranslationKey;
};

export type CompsetRecommendationReasonDetailFullReason = {
  current_rate: number;
  formattedRate: number | RateLike | null;
  formattedRateNumber: number;
  hotelinfo: number;
  name: string;
  price_difference: number;
  price_variance: number;
  reason_type: RecommendationReasonTypeCompset;
  translation_key: RecommendationReasonTranslationKey;
};

export type CompsetRecommendationReasonDetailVsReasons = {
  columns: CustomEmberTableColumn[];
  rows: CustomEmberTableRow[];
};

export type CompsetRecommendationReason = {
  is_increase: boolean | null;
  translation_key: RecommendationReasonTranslationKey;
  values: Record<string, unknown> | null;
} & (
  | {
      details: CompsetRecommendationReasonDetailFallbackReason[];
      reason_type: typeof RECOMMENDATION_REASON_TYPE_COMPSET.FALLBACK;
    }
  | {
      details: CompsetRecommendationReasonDetailFullReason[];
      reason_type: typeof RECOMMENDATION_REASON_TYPE_COMPSET.SOLDOUT;
    }
  | {
      details: CompsetRecommendationReasonDetailFullReason[];
      reason_type:
        | typeof RECOMMENDATION_REASON_TYPE_COMPSET.VS_NORMAL
        | typeof RECOMMENDATION_REASON_TYPE_COMPSET.VS_YESTERDAY;
    }
);

type OccupancyRecommendationReasonDetail = {
  actual: number;
  is_increase: boolean | null;
  occupancy: number;
  reason_type: RecommendationReasonTypeOccupancy;
  room_amount: number;
  translation_key: RecommendationReasonTranslationKey;
  yesterday: number;
};

export type OccupancyRecommendationReason = {
  details: OccupancyRecommendationReasonDetail[];
  is_increase: boolean | null;
  reason_type: RecommendationReasonTypeOccupancy;
  translation_key: RecommendationReasonTranslationKey;
  values: Record<string, unknown> | null;
};

type UserRuleRecommendationReasonDetail = {
  is_increase: boolean | null;
  reason_type: RecommendationReasonTypeUserRule;
  rounding_value: number;
  translation_key: RecommendationReasonTranslationKey;
};

export type UserRuleRecommendationReason = {
  details: UserRuleRecommendationReasonDetail[];
  is_increase: boolean | null;
  reason_type: RecommendationReasonTypeUserRule;
  translation_key: RecommendationReasonTranslationKey;
  values: Record<string, unknown> | null;
};

export default class PaRecommendationReason extends Model {
  [Type] = 'pa-recommendation-reason' as const;

  @belongsTo('carecommendation', {
    async: false,
    inverse: 'paRecommendationReason',
  })
  declare carecommendation: Carecommendation;

  @attr() declare compsetReasons: CompsetRecommendationReason[];
  // Note: Didn't make recommendationReasons as embedded records because:
  // They further have detail objects which are harder to embed since for each reason there can be multiple detail object structures based on reason type
  // Because of this, reason object will have camelCase keys while their detail objects will have snake_case keys which can be confusing when named same and used in same file
  @attr() declare occupancyReasons: OccupancyRecommendationReason[];
  @attr('string') declare summary: RecommendationSummarisedReasons;
  @attr('boolean') declare summaryIsIncrease: boolean;
  @attr() declare userRulesReasons: UserRuleRecommendationReason[];

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  get formattedRecommendationReasonSummary(): string {
    if (!this.hasRecommendationReasonSummary) {
      return '';
    }

    // Create dynamic lokalise key for the summary translation
    // Therefore, make sure new summaries are added in similar format to lokalise
    // const translationKey = `pricing_assistant.reason.summarised.${this.summary}`;
    const translationKey =
      RECOMMENDATION_SUMMARISED_REASONS_TRANSLATION_KEY_MAPPING[this.summary];

    if (this.summary === RECOMMENDATION_SUMMARISED_REASONS.USER_SET_MINIMUM) {
      return this.intl.t(translationKey, {
        minSettingsPrice: this.hotelAndMarketService.formatRate(
          this.carecommendation.carate.userSetMinimum ?? 0,
        ),
        htmlSafe: true,
      });
    }

    if (this.summary === RECOMMENDATION_SUMMARISED_REASONS.USER_SET_MAXIMUM) {
      return this.intl.t(translationKey, {
        maxSettingsPrice: this.hotelAndMarketService.formatRate(
          this.carecommendation.carate.userSetMaximum ?? 0,
        ),
        htmlSafe: true,
      });
    }

    // Check if we have a translation for the specific summary. This ensures BE can add new summaries without breaking the app
    if (!this.intl.exists(translationKey)) {
      // Return empty string if no summary translation is found
      return '';
    }

    return this.intl.t(translationKey);
  }

  get hasCompsetReasons(): boolean {
    return this.compsetReasons.length > 0;
  }

  get hasOccupancyReasons(): boolean {
    return this.occupancyReasons.length > 0;
  }

  get hasRecommendationReasonSummary(): boolean {
    return Boolean(this.summary);
  }

  get hasUserRulesReasons(): boolean {
    return this.userRulesReasons.length > 0;
  }

  get recommendationReasonSummaryTrendingIcon(): IconName {
    return this.summaryIsIncrease ? 'trending-up' : 'trending-down';
  }
}
